import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { reorderColumn } from './TableActions';
import type { TableBlock } from '@/types/report-payload';
import type { DragEvent as ReactDragEvent, RefObject } from 'react';

/** 1×1 transparent GIF used to suppress the browser's default drag ghost. */
const TRANSPARENT_IMG = (() => {
    if (typeof document === 'undefined') return null;
    const img = new Image(1, 1);
    img.src = 'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7';
    return img;
})();

type UseColumnDragProps = {
    disabled: boolean;
    headerLabels: string[];
    block: TableBlock;
    tableRef: RefObject<HTMLTableElement>;
    updateBlock: (next: TableBlock) => void;
};

type UseColumnDragResult = {
    dragOverIndex: number | null;
    dragInsertPosition: 'left' | 'right' | null;
    dragInsertLeft: number | null;
    dragInsertTransitionMs: number;
    dragGhostLabel: string;
    handleColumnDragStart: (colIndex: number, event: ReactDragEvent) => void;
    handleColumnDragEnd: () => void;
};

export const useColumnDrag = ({
    disabled,
    headerLabels,
    block,
    tableRef,
    updateBlock,
}: UseColumnDragProps): UseColumnDragResult => {
    const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);
    const [dragSourceIndex, setDragSourceIndex] = useState<number | null>(null);
    const [dragInsertPosition, setDragInsertPosition] = useState<'left' | 'right' | null>(null);
    const [dragInsertLeft, setDragInsertLeft] = useState<number | null>(null);
    const [dragInsertTransitionMs, setDragInsertTransitionMs] = useState(120);
    const [dragGhostLabel, setDragGhostLabel] = useState('');
    const didDropRef = useRef(false);
    const dragOverIndexRef = useRef<number | null>(null);
    const dragInsertPositionRef = useRef<'left' | 'right' | null>(null);
    const dragOverSpanRef = useRef(1);

    // ── helpers ──────────────────────────────────────────────────────────

    /** Find the header cell closest to `clientX`. Logical column indexes are
     *  read from `data-col-index` because DOM cell order skips merged cells. */
    const findColumnAtX = useCallback(
        (
            clientX: number,
        ): { cell: HTMLTableCellElement; colIndex: number; span: number } | null => {
            // A ÚLTIMA linha do `<thead>`, que é a folha. Com uma faixa de
            // grupo acima dela, a linha 0 traria células que cobrem várias
            // colunas — e o arrasto agarraria a coluna errada.
            const headRows = tableRef.current?.tHead?.rows;
            const headerRow = headRows?.item(headRows.length - 1);
            if (!headerRow || headerRow.cells.length === 0) return null;

            let closest: {
                cell: HTMLTableCellElement;
                colIndex: number;
                span: number;
                dist: number;
            } | null = null;

            for (let i = 0; i < headerRow.cells.length; i++) {
                const cell = headerRow.cells[i];
                const colIndex = Number(cell.dataset.colIndex);
                if (!Number.isFinite(colIndex)) continue;
                const rect = cell.getBoundingClientRect();
                const dist = Math.abs(clientX - (rect.left + rect.width / 2));
                if (!closest || dist < closest.dist) {
                    closest = { cell, colIndex, span: cell.colSpan || 1, dist };
                }
            }

            return closest
                ? { cell: closest.cell, colIndex: closest.colIndex, span: closest.span }
                : null;
        },
        [tableRef],
    );

    /** Compute insertion X using offset* properties (relative to offsetParent). */
    const updateColumnInsertLeft = useCallback(
        (cell: HTMLElement, position: 'left' | 'right') => {
            if (!tableRef.current) return;
            const tableOffsetLeft = tableRef.current.offsetLeft;
            const rawInsert =
                position === 'right'
                    ? tableOffsetLeft + cell.offsetLeft + cell.offsetWidth
                    : tableOffsetLeft + cell.offsetLeft;
            const insideOffset = position === 'right' ? -2 : 2;
            const contentWidth = tableRef.current.scrollWidth || 0;
            const insertOffset = Math.max(0, Math.min(rawInsert + insideOffset, contentWidth));
            setDragInsertLeft((prev) => {
                if (prev === null) {
                    setDragInsertTransitionMs(0);
                } else {
                    const delta = Math.abs(insertOffset - prev);
                    setDragInsertTransitionMs(delta > 80 ? 0 : 120);
                }
                return insertOffset;
            });
        },
        [tableRef],
    );

    const updateColumnIndicatorFromPoint = useCallback(
        (clientX: number) => {
            const hit = findColumnAtX(clientX);
            if (!hit) return;

            const rect = hit.cell.getBoundingClientRect();
            const position: 'left' | 'right' =
                clientX < rect.left + rect.width / 2 ? 'left' : 'right';

            setDragOverIndex(hit.colIndex);
            setDragInsertPosition(position);
            dragOverIndexRef.current = hit.colIndex;
            dragInsertPositionRef.current = position;
            dragOverSpanRef.current = hit.span;
            updateColumnInsertLeft(hit.cell, position);
        },
        [findColumnAtX, updateColumnInsertLeft],
    );

    // ── drag start ──────────────────────────────────────────────────────

    const handleColumnDragStart = useCallback(
        (colIndex: number, event: ReactDragEvent) => {
            if (disabled) return;
            didDropRef.current = false;
            dragOverIndexRef.current = colIndex;
            dragInsertPositionRef.current = 'right';
            dragOverSpanRef.current = 1;
            setDragSourceIndex(colIndex);
            setDragOverIndex(colIndex);
            setDragInsertPosition('right');
            setDragGhostLabel(headerLabels[colIndex] || '');
            setDragInsertTransitionMs(120);
            event.dataTransfer.effectAllowed = 'move';
            event.dataTransfer.setData('text/plain', String(colIndex));

            // Suppress default browser ghost image
            if (TRANSPARENT_IMG) {
                event.dataTransfer.setDragImage(TRANSPARENT_IMG, 0, 0);
            }
        },
        [disabled, headerLabels],
    );

    // ── drop logic ──────────────────────────────────────────────────────

    const applyColumnDrop = useCallback(
        (targetColIndex: number, position: 'left' | 'right', span: number) => {
            if (dragSourceIndex === null) return;
            didDropRef.current = true;

            const columnCount = block.header.cells.length;
            const rawTarget = position === 'right' ? targetColIndex + span : targetColIndex;
            let targetIndex = rawTarget;
            if (dragSourceIndex < targetIndex) {
                targetIndex -= 1;
            }
            targetIndex = Math.max(0, Math.min(targetIndex, columnCount - 1));

            const result = reorderColumn(block, dragSourceIndex, targetIndex);

            if (!result.ok) {
                toast.error('Não é possível reorganizar colunas mescladas.');
            } else if (result.block) {
                updateBlock(result.block);
            }

            setDragSourceIndex(null);
            setDragOverIndex(null);
            setDragInsertPosition(null);
            setDragInsertLeft(null);
            setDragGhostLabel('');
        },
        [block, dragSourceIndex, updateBlock],
    );

    // ── document-level tracking (sole drop zone while a drag is in flight) ─

    useEffect(() => {
        if (disabled || dragSourceIndex === null) return;

        const handleDragOver = (event: DragEvent) => {
            event.preventDefault();
            if (event.dataTransfer) event.dataTransfer.dropEffect = 'move';
            updateColumnIndicatorFromPoint(event.clientX);
        };
        const handleDrop = (event: DragEvent) => {
            event.preventDefault();
            if (dragOverIndexRef.current !== null) {
                applyColumnDrop(
                    dragOverIndexRef.current,
                    dragInsertPositionRef.current ?? 'right',
                    dragOverSpanRef.current,
                );
            }
        };

        document.addEventListener('dragover', handleDragOver);
        document.addEventListener('drop', handleDrop);
        return () => {
            document.removeEventListener('dragover', handleDragOver);
            document.removeEventListener('drop', handleDrop);
        };
    }, [applyColumnDrop, disabled, dragSourceIndex, updateColumnIndicatorFromPoint]);

    // ── drag end ────────────────────────────────────────────────────────

    const handleColumnDragEnd = useCallback(() => {
        if (
            !didDropRef.current &&
            dragOverIndexRef.current !== null &&
            dragInsertPositionRef.current
        ) {
            applyColumnDrop(
                dragOverIndexRef.current,
                dragInsertPositionRef.current,
                dragOverSpanRef.current,
            );
        }
        setDragSourceIndex(null);
        setDragOverIndex(null);
        setDragInsertPosition(null);
        setDragInsertLeft(null);
        setDragGhostLabel('');
    }, [applyColumnDrop]);

    return {
        dragOverIndex,
        dragInsertPosition,
        dragInsertLeft,
        dragInsertTransitionMs,
        dragGhostLabel,
        handleColumnDragStart,
        handleColumnDragEnd,
    };
};
